<script setup>
import { computed, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { emitter } from 'shared/helpers/mitt';
import wootConstants from 'dashboard/constants/globals';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_PIN_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
  CMD_UNPIN_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const isPinnedGetter = useMapGetter('conversationPins/isPinned');
const isPinned = computed(() => isPinnedGetter.value(currentChat.value.id));
// Resolving drops the pins, so pinning a resolved conversation would only burn one of the five slots.
const canTogglePin = computed(
  () =>
    isPinned.value ||
    currentChat.value.status !== wootConstants.STATUS_TYPE.RESOLVED
);

const pin = async () => {
  try {
    await store.dispatch('conversationPins/pin', currentChat.value.id);
  } catch (error) {
    useAlert(error?.message ?? t('CONVERSATION.PIN.ERROR'));
  }
};

const unpin = async () => {
  try {
    await store.dispatch('conversationPins/unpin', currentChat.value.id);
  } catch (error) {
    useAlert(error?.message ?? t('CONVERSATION.PIN.ERROR'));
  }
};

const togglePin = () => {
  if (!canTogglePin.value) return undefined;

  return isPinned.value ? unpin() : pin();
};

useKeyboardEvents({
  'Alt+KeyI': { action: togglePin },
});

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  if (canTogglePin.value) {
    items.push({
      icon: isPinned.value ? 'i-lucide-pin-off' : 'i-lucide-pin',
      label: isPinned.value
        ? t('CONVERSATION.PIN.UNPIN')
        : t('CONVERSATION.PIN.PIN'),
      action: 'toggle_pin',
      value: 'toggle_pin',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  if (isAdmin.value) {
    items.push({
      icon: 'i-lucide-download',
      label: t('CONTACT_PANEL.EXPORT_CONVERSATION'),
      action: 'export_conversation',
      value: 'export_conversation',
    });
  }

  return items;
});

const handleActionClick = async ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'toggle_pin') {
    togglePin();
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'export_conversation') {
    try {
      await store.dispatch('exportConversation', currentChat.value.id);
      useAlert(t('CONTACT_PANEL.EXPORT_CONVERSATION_SUCCESS'));
    } catch (error) {
      useAlert(t('CONTACT_PANEL.EXPORT_CONVERSATION_ERROR'));
    }
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_PIN_CONVERSATION, pin);
emitter.on(CMD_UNPIN_CONVERSATION, unpin);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_PIN_CONVERSATION, pin);
  emitter.off(CMD_UNPIN_CONVERSATION, unpin);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="!size-9 !rounded-[9px] !text-n-slate-11 group-hover:bg-n-slate-3"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
